export type ReportWorkflowStatus =
  | "NOT_STARTED"
  | "DRAFT"
  | "SUBMITTED"
  | "NEEDS_ATTENTION"
  | "REVIEW_REQUESTED"
  | "NEEDS_REVIEW"
  | "RETURNED"
  | "REJECTED"
  | "AWAITING_APPROVAL"
  | "APPROVED"
  | "FINAL"
  | string;

function toStatusToken(status?: string | null): ReportWorkflowStatus {
  return String(status ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

export function normalizeReportWorkflowStatus(
  status?: string | null
): ReportWorkflowStatus {
  return toStatusToken(status);
}

/** True when no department_report row exists for the selected period. */
export function isDepartmentReportMissing(
  departmentReportId?: number | null
): boolean {
  return departmentReportId == null || departmentReportId <= 0;
}

/** Resolve the workflow status shown in the UI when a report may not exist yet. */
export function resolveDepartmentReportDisplayStatus(
  departmentReportId?: number | null,
  status?: string | null
): ReportWorkflowStatus {
  if (isDepartmentReportMissing(departmentReportId)) {
    return "NOT_STARTED";
  }
  return normalizeReportWorkflowStatus(status);
}

export function isCorrectionWorkflowStatus(status?: string | null): boolean {
  const normalized = toStatusToken(status);
  return (
    normalized === "NEEDS_ATTENTION" ||
    normalized === "REVIEW_REQUESTED" ||
    normalized === "NEEDS_REVIEW" ||
    normalized === "RETURNED" ||
    normalized === "REJECTED"
  );
}

/**
 * True when a report tag's status is CLOSED. Used to pre-gate save/submit in
 * the UI (the API already rejects writes against a CLOSED tag); this avoids
 * surprising the user with a server-side error.
 */
export function isTagClosed(tagStatusName?: string | null): boolean {
  return toStatusToken(tagStatusName) === "CLOSED";
}

/**
 * True when a report tag is OPEN for submissions. The API rejects writes for
 * PENDING and CLOSED tags; UI should hide Save/Submit unless this is true.
 */
export function isTagOpen(tagStatusName?: string | null): boolean {
  return toStatusToken(tagStatusName) === "OPEN";
}

/**
 * Format a status label for UI display: replace `_`/`-` with spaces, then
 * apply casing. Default is title case (`Awaiting Approval`).
 */
export type StatusDisplayCase = "upper" | "title" | "lower" | "sentence";

export function formatStatusDisplay(
  label: string,
  casing: StatusDisplayCase = "title"
): string {
  const spaced = label
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!spaced) return "";

  const lower = spaced.toLowerCase();
  switch (casing) {
    case "lower":
      return lower;
    case "sentence":
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    case "title":
      return lower
        .split(" ")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    case "upper":
    default:
      return spaced.toUpperCase();
  }
}

/** Display label for a report tag status (default: Open / Closed / Pending). */
export function getReportTagStatusLabel(
  tagStatusName?: string | null,
  casing: StatusDisplayCase = "title"
): string {
  const token = toStatusToken(tagStatusName);
  if (!token) return "";
  const base = token
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
  return formatStatusDisplay(base, casing);
}

/** Muted header suffix: ` · Open` (empty when status is missing). */
export function formatReportTagPeriodSuffix(tagStatusName?: string | null): string {
  const label = getReportTagStatusLabel(tagStatusName);
  return label ? ` · ${label}` : "";
}

export function isDepartmentReportEditable(status?: string | null): boolean {
  const normalized = toStatusToken(status);
  return (
    normalized === "" ||
    normalized === "NOT_STARTED" ||
    normalized === "DRAFT" ||
    isCorrectionWorkflowStatus(normalized)
  );
}

export function canSubmitDepartmentReport(status?: string | null): boolean {
  const normalized = toStatusToken(status);
  return (
    normalized !== "AWAITING_APPROVAL" &&
    normalized !== "APPROVED" &&
    normalized !== "FINAL"
  );
}

export function canValidateDepartmentReport(status?: string | null): boolean {
  const normalized = toStatusToken(status);
  return normalized === "SUBMITTED" || isCorrectionWorkflowStatus(normalized);
}

export function canApproveDepartmentReport(status?: string | null): boolean {
  return toStatusToken(status) === "AWAITING_APPROVAL";
}

export function canRequestDepartmentReportReview(status?: string | null): boolean {
  return normalizeReportWorkflowStatus(status) === "AWAITING_APPROVAL";
}

/**
 * Row-level request review for a child report (e.g. nation → district,
 * district → church). Matches API: target must not be DRAFT or NEEDS_ATTENTION
 * (including correction-status synonyms).
 */
export function canRequestChildDepartmentReportReview(
  status?: string | null
): boolean {
  const normalized = toStatusToken(status);
  if (!normalized || normalized === "NOT_STARTED" || normalized === "DRAFT") {
    return false;
  }
  if (isCorrectionWorkflowStatus(normalized)) {
    return false;
  }
  return true;
}

/**
 * Ancestor gate for child request-review. Parent may be missing, or DRAFT /
 * NEEDS_ATTENTION (and correction synonyms). Locked parents (awaiting
 * approval / approved) block child review.
 */
export function isAncestorOpenForChildReview(
  parentReportId?: number | null,
  parentStatus?: string | null
): boolean {
  if (isDepartmentReportMissing(parentReportId)) {
    return true;
  }
  const normalized = toStatusToken(parentStatus);
  return (
    normalized === "DRAFT" ||
    normalized === "" ||
    isCorrectionWorkflowStatus(normalized)
  );
}

/**
 * Reports may only be downloaded / printed once approved. Shared by the form
 * reports (Minister's, Finance & Statistics) and the long-table PDF download.
 */
export function canDownloadReport(status?: string | null): boolean {
  const normalized = toStatusToken(status);
  return normalized === "APPROVED" || normalized === "FINAL";
}

export function canEditActivitySubmission(
  activityStatus?: string | null,
  parentDepartmentStatus?: string | null
): boolean {
  const normalized = toStatusToken(activityStatus);

  if (
    normalizeReportWorkflowStatus(parentDepartmentStatus) ===
      "AWAITING_APPROVAL" ||
    normalizeReportWorkflowStatus(parentDepartmentStatus) === "APPROVED" ||
    normalizeReportWorkflowStatus(parentDepartmentStatus) === "FINAL"
  ) {
    return false;
  }

  return (
    normalized === "" ||
    normalized === "NOT_STARTED" ||
    normalized === "DRAFT" ||
    isCorrectionWorkflowStatus(normalized)
  );
}

export function canRequestActivityReview(
  activityStatus?: string | null,
  parentDepartmentStatus?: string | null
): boolean {
  return (
    toStatusToken(activityStatus) === "SUBMITTED" &&
    isDepartmentReportEditable(parentDepartmentStatus)
  );
}

export function getWorkflowStatusLabel(
  status?: string | null,
  casing: StatusDisplayCase = "title"
): string {
  const normalized = toStatusToken(status);

  let base: string;
  switch (normalized) {
    case "":
    case "NOT_STARTED":
      base = "Not Started";
      break;
    case "DRAFT":
      base = "Draft";
      break;
    case "SUBMITTED":
      base = "Submitted";
      break;
    case "NEEDS_ATTENTION":
    case "REVIEW_REQUESTED":
    case "NEEDS_REVIEW":
    case "RETURNED":
    case "REJECTED":
      base = "Needs Attention";
      break;
    case "AWAITING_APPROVAL":
      base = "Awaiting Approval";
      break;
    case "APPROVED":
    case "FINAL":
      base = "Approved";
      break;
    default:
      base = normalized
        .toLowerCase()
        .split("_")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
  }
  return formatStatusDisplay(base, casing);
}

/** Single status + time line for report timeline events (UI and PDF). */
export function formatTimelineEventSubtitle(ev: {
  status_name?: string | null;
  kind: string;
  at?: string | null;
}): string {
  const status = (ev.status_name ?? "").trim();
  const label = status
    ? getWorkflowStatusLabel(status)
    : getWorkflowStatusLabel(ev.kind) || ev.kind;
  return `${label}${ev.at ? ` · ${ev.at}` : " · outstanding"}`;
}

export function getWorkflowStatusVariant(status?: string | null): string {
  const normalized = toStatusToken(status);

  if (normalized === "" || normalized === "NOT_STARTED") return "dark";
  // dark (not secondary): NTCOG remaps Bootstrap secondary to brand amber.
  if (normalized === "DRAFT") return "dark";
  if (normalized === "SUBMITTED") return "primary";
  if (isCorrectionWorkflowStatus(normalized)) return "warning";
  if (normalized === "AWAITING_APPROVAL") return "info";
  if (normalized === "APPROVED" || normalized === "FINAL") return "success";
  return "secondary";
}

export function getWorkflowEditabilityText(
  editable: boolean,
  status?: string | null
): string {
  if (editable) {
    return "Editable";
  }

  const normalized = toStatusToken(status);
  if (normalized === "SUBMITTED") {
    return `${getWorkflowStatusLabel("SUBMITTED")} items are view-only until review is requested.`;
  }
  if (normalized === "AWAITING_APPROVAL") {
    return `${getWorkflowStatusLabel("AWAITING_APPROVAL")} items are view-only.`;
  }
  if (normalized === "APPROVED" || normalized === "FINAL") {
    return `${getWorkflowStatusLabel("APPROVED")} items are view-only.`;
  }

  return "This item is view-only.";
}

/**
 * Single editability explanation for activity entry forms. Parent department
 * report locks take priority over activity-level status messages.
 */
export type ReportProcessResults = {
  details?: string | Record<string, unknown> | null;
  message?: string | null;
};

function isJsonEncodedString(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
}

/**
 * Extract the human-readable reason shown when a report is in NEEDS_ATTENTION.
 * Prefers plain-text details (review notes); skips JSON-encoded validation payloads.
 */
export function getNeedsAttentionReason(
  statusName: string | null | undefined,
  processResults?: ReportProcessResults | null
): string | null {
  if (normalizeReportWorkflowStatus(statusName) !== "NEEDS_ATTENTION") {
    return null;
  }

  const details =
    typeof processResults?.details === "string"
      ? processResults.details.trim()
      : "";
  const message =
    typeof processResults?.message === "string"
      ? processResults.message.trim()
      : "";

  if (details && !isJsonEncodedString(details)) return details;
  if (message) return message;
  if (details) return details;
  return null;
}

export type NeedsAttentionItem = {
  activityId: number | null;
  activityName: string | null;
  kpiId: number | null;
  kpiName: string | null;
  subGroupId: number | null;
  subGroupName: string | null;
};

export type NeedsAttentionTemplate = {
  templateId: number;
  templateName: string;
  items: NeedsAttentionItem[];
};

export type NeedsAttentionChild = {
  id: number;
  name: string;
  childScope: string;
};

export type NeedsAttentionChurch = {
  churchId: number;
  churchName: string;
  templates: NeedsAttentionTemplate[];
};

export type NeedsAttentionDistrict = {
  districtId: number;
  districtName: string;
  churches: NeedsAttentionChurch[];
};

export type NeedsAttentionDetails = {
  templates: NeedsAttentionTemplate[];
  officeTemplates: NeedsAttentionTemplate[];
  unapprovedChildren: NeedsAttentionChild[];
  churches: NeedsAttentionChurch[];
  districts: NeedsAttentionDistrict[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toNullableNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function toNullableString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function readDetailsPayload(details: ReportProcessResults["details"]): Record<string, unknown> | null {
  if (isRecord(details)) return details;
  if (typeof details !== "string") return null;

  const trimmed = details.trim();
  if (!isJsonEncodedString(trimmed)) return null;

  try {
    const parsed: unknown = JSON.parse(trimmed);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function normalizeItem(raw: Record<string, unknown>): NeedsAttentionItem {
  return {
    activityId: toNullableNumber(raw.activity_id),
    activityName: toNullableString(raw.activity_name),
    kpiId: toNullableNumber(raw.kpi_id ?? raw.missing_kpi_id),
    kpiName: toNullableString(raw.kpi_name ?? raw.missing_kpi_name),
    subGroupId: toNullableNumber(raw.sub_group_id),
    subGroupName: toNullableString(raw.sub_group_name),
  };
}

function groupMissingSubmissions(rows: unknown): NeedsAttentionTemplate[] {
  if (!Array.isArray(rows)) return [];

  const byTemplate = new Map<number, NeedsAttentionTemplate>();

  for (const row of rows) {
    if (!isRecord(row)) continue;
    const templateId = toNullableNumber(row.template_id);
    if (templateId === null) continue;

    let template = byTemplate.get(templateId);
    if (!template) {
      template = {
        templateId,
        templateName: toNullableString(row.template_name) ?? "",
        items: [],
      };
      byTemplate.set(templateId, template);
    }

    const hasActivityOrKpi =
      toNullableNumber(row.activity_id) !== null ||
      toNullableNumber(row.missing_kpi_id ?? row.kpi_id) !== null;
    if (!hasActivityOrKpi) continue;

    template.items.push(normalizeItem(row));
  }

  return [...byTemplate.values()];
}

function mapNestedTemplates(rows: unknown): NeedsAttentionTemplate[] | null {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  if (!rows.some((row) => isRecord(row) && "items" in row)) return null;

  const templates: NeedsAttentionTemplate[] = [];
  for (const row of rows) {
    if (!isRecord(row)) continue;
    const templateId = toNullableNumber(row.template_id);
    if (templateId === null) continue;

    const items = Array.isArray(row.items)
      ? row.items.filter(isRecord).map(normalizeItem)
      : [];

    templates.push({
      templateId,
      templateName: toNullableString(row.template_name) ?? "",
      items,
    });
  }

  return templates;
}

function parseTemplateList(source: Record<string, unknown>): NeedsAttentionTemplate[] {
  const nested = mapNestedTemplates(source.incomplete_templates);
  if (nested) return nested;

  const fromMissing = groupMissingSubmissions(source.missing_submissions);
  if (fromMissing.length > 0) return fromMissing;

  if (!Array.isArray(source.incomplete_templates)) return [];
  return source.incomplete_templates.flatMap((row) => {
    if (!isRecord(row)) return [];
    const templateId = toNullableNumber(row.template_id);
    if (templateId === null) return [];
    return [
      {
        templateId,
        templateName: toNullableString(row.template_name) ?? "",
        items: [],
      },
    ];
  });
}

function parseUnapprovedChildren(value: unknown): NeedsAttentionChild[] {
  if (!Array.isArray(value)) return [];
  const children: NeedsAttentionChild[] = [];
  for (const row of value) {
    if (!isRecord(row)) continue;
    const id = toNullableNumber(row.id);
    const name = toNullableString(row.name);
    if (id === null || name === null) continue;
    children.push({
      id,
      name,
      childScope: toNullableString(row.child_scope) ?? "",
    });
  }
  return children;
}

function parseChurches(value: unknown): NeedsAttentionChurch[] {
  if (!Array.isArray(value)) return [];
  const churches: NeedsAttentionChurch[] = [];
  for (const row of value) {
    if (!isRecord(row) || row.skipped_inactive === true) continue;
    const churchId = toNullableNumber(row.church_id);
    const churchName = toNullableString(row.church_name);
    if (churchId === null || churchName === null) continue;
    const templates = parseTemplateList(row);
    if (templates.length === 0) continue;
    churches.push({ churchId, churchName, templates });
  }
  return churches;
}

function parseDistricts(value: unknown): NeedsAttentionDistrict[] {
  if (!Array.isArray(value)) return [];
  const districts: NeedsAttentionDistrict[] = [];
  for (const row of value) {
    if (!isRecord(row) || row.skipped_inactive === true) continue;
    const districtId = toNullableNumber(row.district_id);
    const districtName = toNullableString(row.district_name);
    if (districtId === null || districtName === null) continue;
    const churches = parseChurches(row.churches);
    if (churches.length === 0) continue;
    districts.push({ districtId, districtName, churches });
  }
  return districts;
}

/**
 * Structured Needs Attention gaps from the last process log or live process response.
 * Returns null for review notes, invalid JSON, or payloads with nothing to show.
 */
export function parseNeedsAttentionDetails(
  processResults?: ReportProcessResults | null
): NeedsAttentionDetails | null {
  const payload = readDetailsPayload(processResults?.details);
  if (!payload) return null;

  const officeSource = isRecord(payload.office) ? payload.office : null;
  const details: NeedsAttentionDetails = {
    templates: parseTemplateList(payload),
    officeTemplates: officeSource ? parseTemplateList(officeSource) : [],
    unapprovedChildren: parseUnapprovedChildren(payload.unapproved_required_children),
    churches: parseChurches(payload.churches),
    districts: parseDistricts(payload.districts),
  };

  if (
    details.templates.length === 0 &&
    details.officeTemplates.length === 0 &&
    details.unapprovedChildren.length === 0 &&
    details.churches.length === 0 &&
    details.districts.length === 0
  ) {
    return null;
  }

  return details;
}

const APPROVAL_NOTES_PREFIX = "Approval notes: ";

/**
 * Resolve approval notes for display on department report screens.
 * Prefers submission.approval_notes; falls back to process_results.details
 * written as "Approval notes: …" (legacy path when approval.notes was not persisted).
 */
export function getApprovalNotes(
  approvalNotes?: string | null,
  processResults?: ReportProcessResults | null
): string | null {
  const fromSubmission =
    typeof approvalNotes === "string" ? approvalNotes.trim() : "";
  if (fromSubmission) return fromSubmission;

  const details =
    typeof processResults?.details === "string"
      ? processResults.details.trim()
      : "";
  if (details.startsWith(APPROVAL_NOTES_PREFIX)) {
    const legacy = details.slice(APPROVAL_NOTES_PREFIX.length).trim();
    return legacy || null;
  }
  return null;
}

export function getActivityEditabilityText(
  activityEditable: boolean,
  activityStatus?: string | null,
  parentDepartmentStatus?: string | null,
  parentDepartmentStatusLabel?: string | null
): string {
  if (activityEditable) {
    return "Editable";
  }

  const normalizedParentStatus = normalizeReportWorkflowStatus(
    parentDepartmentStatus
  );

  if (
    normalizedParentStatus === "AWAITING_APPROVAL" ||
    normalizedParentStatus === "APPROVED" ||
    normalizedParentStatus === "FINAL"
  ) {
    const label =
      parentDepartmentStatusLabel ??
      getWorkflowStatusLabel(parentDepartmentStatus);
    return `The linked department report is ${label.toLowerCase()}, so this activity submission is view-only.`;
  }

  return getWorkflowEditabilityText(false, activityStatus);
}

/** True when both ids are positive numbers and refer to the same user. */
export function isCurrentUserSubmitter(
  currentUserId: number | null | undefined,
  submittedBy: number | string | null | undefined
): boolean {
  const userId = Number(currentUserId);
  const submitterId = Number(submittedBy);
  return (
    Number.isFinite(userId) &&
    userId > 0 &&
    Number.isFinite(submitterId) &&
    submitterId > 0 &&
    userId === submitterId
  );
}

/**
 * Label for the shared request-review action: Request Edit when the current
 * user submitted the record, otherwise Request Review.
 */
export type RequestReviewCopy = {
  actionLabel: "Request Edit" | "Request Review";
  noun: "edit" | "review";
  reasonLabel: "Reason for Edit" | "Reason for Review";
  confirmLabel: "Confirm Edit" | "Confirm Request";
};

export function getRequestReviewCopy(
  currentUserId: number | null | undefined,
  submittedBy: number | string | null | undefined
): RequestReviewCopy {
  if (isCurrentUserSubmitter(currentUserId, submittedBy)) {
    return {
      actionLabel: "Request Edit",
      noun: "edit",
      reasonLabel: "Reason for Edit",
      confirmLabel: "Confirm Edit",
    };
  }
  return {
    actionLabel: "Request Review",
    noun: "review",
    reasonLabel: "Reason for Review",
    confirmLabel: "Confirm Request",
  };
}

export function getRequestReviewLabel(
  currentUserId: number | null | undefined,
  submittedBy: number | string | null | undefined
): "Request Edit" | "Request Review" {
  return getRequestReviewCopy(currentUserId, submittedBy).actionLabel;
}
